import { useConversationImport } from "@/hooks/use-conversation-import";
import { rulebaseApi2Request } from "@/lib/rulebase-api2-client/client";
import type { Conversation } from "@rulebase/core/api";
import { Button } from "@rulebase/core/components";
import { useQuery } from "@tanstack/react-query";
import { IconLoadingCircle } from "central-icons";
import { useEffect, useMemo, useState } from "react";
import { RiTicketLine } from "react-icons/ri";
import { CallDetailView } from "./call-detail-view";
import { KBChatView } from "./kb-chat-view";
import { formatTicketLabel, type TicketContext } from "./ticket-context";
import { useHasLinkedActivity } from "@/hooks/use-linked-activity";
import { useWorkspaceIdentity } from "@/hooks/use-workspace";
import { LinkedActivityView } from "./linked-activity-view";
import { RecentCallsView } from "./recent-calls-view";
import { TicketHeader } from "./ticket-header";
import { TicketPromptInput } from "./ticket-prompt-input";
import { Empty, EmptyDescription, EmptyIcon, EmptyTitle } from "./ui/empty";
import { TooltipProvider } from "./ui/tooltip";
import { WorkspaceHeader } from "./workspace-header";

export default function SidebarPage() {
  return (
    <TooltipProvider>
      <ActionView />
    </TooltipProvider>
  );
}

function ActionView() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlTicketId = urlParams.get("ticket-id");
  const [ticketId, setTicketId] = useState<string>(urlTicketId || "");

  useEffect(() => {
    // announce readiness
    window.parent.postMessage({ type: "RULEBASE::IFRAME_READY" }, "*");

    const handleMessage = (event: MessageEvent) => {
      console.log("Received message event", event);
      if (event.data?.type === "RULEBASE::ZENDESK_TICKET") {
        const ticket = event.data.ticket;
        console.log("Got ticket in iframe:", ticket);
        setTicketId(ticket.id?.toString());

        // acknowledge receipt
        window.parent.postMessage({ type: "RULEBASE::TICKET_RECEIVED" }, "*");
      }
    };

    console.log("Listening for message events...");
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const {
    conversation: conversationSummary,
    isLoading,
    isImporting,
    error: conversationImportError,
    retry: retryConversationImport,
  } = useConversationImport({
    ticketId,
  });

  const {
    data: conversationData,
    isPending: isConversationPending,
    isFetching: isConversationFetching,
  } = useQuery({
    queryKey: ["conversations", conversationSummary?.id],
    queryFn: () => {
      if (!conversationSummary?.id) {
        throw new Error("Conversation ID is required");
      }

      return rulebaseApi2Request<{ data: Conversation }>(
        `/conversations/${encodeURIComponent(conversationSummary.id)}`,
      );
    },
    enabled: !!conversationSummary?.id,
  });

  const isConversationLoading =
    !!conversationSummary?.id &&
    (isConversationPending || isConversationFetching);

  if (!ticketId || isLoading || isImporting || isConversationLoading) {
    return (
      <div className="flex h-full min-h-[50vh] w-full items-center justify-center">
        <IconLoadingCircle className="size-10 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (!conversationData) {
    return (
      <Empty className="h-full">
        <EmptyIcon>
          <RiTicketLine />
        </EmptyIcon>
        <div className="flex flex-col gap-1">
          <EmptyTitle>No ticket found</EmptyTitle>
          <EmptyDescription>
            {conversationImportError
              ? "We couldn't import this ticket. Try again."
              : "We couldn't find a matching ticket for this conversation."}
          </EmptyDescription>
        </div>
        {conversationImportError ? (
          <Button onClick={retryConversationImport}>Try again</Button>
        ) : null}
      </Empty>
    );
  }

  return (
    <ActionViewForConversation conversationId={conversationData.data.id} />
  );
}

function ActionViewForConversation(props: { conversationId: string }) {
  // ActionView loads this same query first, so it is normally already cached.
  const { data, isError, refetch } = useQuery({
    queryKey: ["conversations", props.conversationId],
    queryFn: () =>
      rulebaseApi2Request<{ data: Conversation }>(
        `/conversations/${encodeURIComponent(props.conversationId)}`,
      ),
  });

  if (data) return <ConversationActionView conversation={data.data} />;

  if (isError) {
    return (
      <Empty className="h-full">
        <EmptyIcon>
          <RiTicketLine />
        </EmptyIcon>
        <div className="flex flex-col gap-1">
          <EmptyTitle>Couldn&apos;t load this ticket</EmptyTitle>
        </div>
        <Button onClick={() => void refetch()}>Try again</Button>
      </Empty>
    );
  }

  return (
    <div className="flex h-full min-h-[50vh] w-full items-center justify-center">
      <IconLoadingCircle className="size-10 animate-spin text-neutral-500" />
    </div>
  );
}

function ConversationActionView({
  conversation,
}: {
  conversation: Conversation;
}) {
  const [showKBChat, setShowKBChat] = useState(false);
  const [kbInitialPrompt, setKbInitialPrompt] = useState<string | undefined>();
  const [promptInput, setPromptInput] = useState("");
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [linksOpen, setLinksOpen] = useState(true);
  const hasLinkedActivity = useHasLinkedActivity(conversation);

  const { organizationName } = useWorkspaceIdentity();
  const customerName =
    conversation.requester?.name ?? conversation.clients[0]?.name;
  const companyName = conversation.company?.name;

  // What the prompt offers to search, cycled in the placeholder. Anything the
  // ticket doesn't have simply drops out of the rotation.
  const subjectLabels = useMemo(
    () =>
      [
        "this ticket",
        customerName,
        companyName,
        organizationName ? `${organizationName}'s knowledge base` : null,
      ].filter((label): label is string => !!label),
    [customerName, companyName, organizationName],
  );

  const isJira = conversation.data_source?.id === "jira";
  const ticketNumber =
    isJira && conversation.jira_issue_key
      ? conversation.jira_issue_key
      : conversation.external_id;
  const ticketContext: TicketContext | undefined = ticketNumber
    ? {
        conversationId: conversation.id,
        ticketLabel: formatTicketLabel(ticketNumber),
      }
    : undefined;

  if (openCallId) {
    return (
      <CallDetailView callId={openCallId} onBack={() => setOpenCallId(null)} />
    );
  }

  if (showKBChat) {
    return (
      <KBChatView
        initialPrompt={kbInitialPrompt}
        ticketContext={ticketContext}
        onBack={() => {
          setShowKBChat(false);
          setKbInitialPrompt(undefined);
        }}
      />
    );
  }

  function handlePromptSubmit() {
    const text = promptInput.trim();
    if (!text) return;
    setKbInitialPrompt(text);
    setPromptInput("");
    setShowKBChat(true);
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col px-2 py-5">
          <WorkspaceHeader />

          <div className="mt-4">
            <TicketPromptInput
              value={promptInput}
              onValueChange={setPromptInput}
              onSubmit={handlePromptSubmit}
              subjectLabels={subjectLabels}
            />
          </div>

          <div className="mt-5">
            <TicketHeader
              conversation={conversation}
              ticketNumber={ticketNumber}
              customerName={customerName}
              companyName={companyName}
              canToggleLinks={hasLinkedActivity}
              linksOpen={linksOpen}
              onToggleLinks={() => setLinksOpen((open) => !open)}
            />
          </div>

          <div className="mt-2">
            <LinkedActivityView conversation={conversation} open={linksOpen} />
          </div>

          <RecentCallsView onOpenCall={setOpenCallId} />
        </div>
      </div>
    </div>
  );
}
